import { IconButton, useTheme } from '@ltvco/refresh-lib/theme';
import { AttachFile } from '@mui/icons-material';
import { useRef, ChangeEvent } from 'react';

import { getUploadFileValidation } from 'components/ChatBV/utils';

interface ImageUploadButtonProps {
  onImageSelect: (file: File) => void;
  onError?: (errorMessage: string) => void;
  disabled?: boolean;
}

export const ImageUploadButton = ({
  onImageSelect,
  onError,
  disabled = false,
}: ImageUploadButtonProps) => {
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    const { isValidFile, isValidSize } = getUploadFileValidation(file);

    if (isValidFile && isValidSize) {
      onImageSelect(file!);
    } else if (file && !isValidFile) {
      if (onError) {
        onError(
          "We couldn't upload your file. Please upload an image or PDF file."
        );
      }
    } else if (isValidFile && !isValidSize) {
      if (onError) {
        onError(
          "We couldn't upload your file. Check that the file is under 32 MB and try again."
        );
      }
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,.pdf,application/pdf"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
      <IconButton
        onClick={handleClick}
        disabled={disabled}
        sx={{
          color: theme.palette.text.secondary,
          padding: '8px',
          marginLeft: 0,
          marginRight: 0,
          '&:hover': {
            color: theme.palette.primary.main,
            backgroundColor: 'transparent',
          },
          '&:disabled': {
            color: theme.palette.text.disabled,
          },
        }}
        aria-label="Upload image"
      >
        <AttachFile sx={{ fontSize: '20px' }} />
      </IconButton>
    </>
  );
};
